import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Avatar,
  Stack,
  useTheme,
} from '@mui/material';
import { Download, Email, EmojiEvents } from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import CertificatesModal from './Certificates';

const Hero = () => {
  const theme = useTheme();
  const { darkMode } = useThemeMode();
  const [certificatesOpen, setCertificatesOpen] = useState(false);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: darkMode
          ? 'linear-gradient(135deg, rgba(100, 181, 246, 0.1) 0%, rgba(244, 143, 177, 0.1) 100%)'
          : 'linear-gradient(135deg, rgba(25, 118, 210, 0.08) 0%, rgba(220, 0, 78, 0.08) 100%)',
        pt: 8,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: darkMode
            ? 'radial-gradient(circle at 20% 80%, rgba(100, 181, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(244, 143, 177, 0.1) 0%, transparent 50%)'
            : 'radial-gradient(circle at 20% 80%, rgba(25, 118, 210, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(220, 0, 78, 0.05) 0%, transparent 50%)',
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            gap: 6,
          }}
        >
          <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem', lg: '4rem' },
                fontWeight: 800,
                mb: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
                whiteSpace: { xs: 'normal', sm: 'nowrap' },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              Hi, I'm Yogesh More
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: { xs: 'center', md: 'flex-start' },
              gap: 2,
              mb: 1,
              flexWrap: 'wrap'
            }}>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.2rem' },
                  color: 'text.primary',
                  fontWeight: 600,
                }}
              >
                Frontend Developer
              </Typography>
              
              {/* Coding Platform Icons */}
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Box
                  component="a"
                  href="https://leetcode.com/u/iamyogeshmore/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: darkMode
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(255, 193, 7, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px) scale(1.1)',
                      boxShadow: '0 4px 12px rgba(255, 193, 7, 0.3)',
                      border: '1px solid rgba(255, 193, 7, 0.6)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/icons/Leetcode_Logo.png"
                    alt="LeetCode"
                    sx={{ 
                      width: 18, 
                      height: 18,
                      objectFit: 'contain'
                    }}
                  />
                </Box>

                <Box
                  component="a"
                  href="https://www.hackerrank.com/profile/iyogesh_more"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: darkMode
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(255, 255, 255, 0.9)',
                    border: '1px solid rgba(0, 234, 100, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px) scale(1.1)',
                      boxShadow: '0 4px 12px rgba(0, 234, 100, 0.3)',
                      border: '1px solid rgba(0, 234, 100, 0.6)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/icons/HackerRank_Icon-1000px.png"
                    alt="HackerRank"
                    sx={{ 
                      width: 18, 
                      height: 18,
                      objectFit: 'contain'
                    }}
                  />
                </Box>

                <Box
                  component="a"
                  href="https://github.com/iamyogeshmore"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: darkMode
                      ? 'rgba(255, 255, 255, 0.08)'
                      : 'rgba(255, 255, 255, 0.9)',
                    border: darkMode 
                      ? '1px solid rgba(255, 255, 255, 0.3)'
                      : '1px solid rgba(51, 51, 51, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px) scale(1.1)',
                      boxShadow: darkMode
                        ? '0 4px 12px rgba(255, 255, 255, 0.3)'
                        : '0 4px 12px rgba(51, 51, 51, 0.3)',
                      border: darkMode 
                        ? '1px solid rgba(255, 255, 255, 0.6)'
                        : '1px solid rgba(51, 51, 51, 0.6)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/icons/GitHub.png"
                    alt="GitHub"
                    sx={{ 
                      width: 18, 
                      height: 18,
                      filter: darkMode ? 'invert(1)' : 'none',
                    }}
                  />
                </Box>
              </Stack>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                mb: 4,
                color: 'text.secondary',
                fontWeight: 400,
                fontFamily: '"JetBrains Mono", monospace',
                textAlign: { xs: 'center', md: 'left' },
                wordBreak: { xs: 'break-word', md: 'normal' },
                '&::after': {
                  content: '"_"',
                  animation: 'terminalBlink 1s infinite',
                  color: 'primary.main',
                  fontWeight: 'bold',
                },
              }}
            >
              $ echo "Building modern React applications"
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.1rem' },
                mb: 4,
                maxWidth: '600px',
                lineHeight: 1.7,
                color: 'text.secondary',
                textAlign: { xs: 'center', md: 'left' },
                px: { xs: 1, md: 0 },
              }}
            >
              Passionate React developer specializing in creating dynamic, responsive, and user-friendly web applications. 
              Experienced in modern JavaScript, React ecosystem, and frontend technologies. 
              I love turning complex problems into simple, beautiful, and intuitive solutions.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ justifyContent: { xs: 'center', md: 'flex-start' }, mb: 4 }}
            >
              <Button
                variant="contained"
                size="medium"
                startIcon={<Download />}
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = '/Yogesh_More_Resume.pdf';
                  link.download = 'Yogesh_More_Resume.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                sx={{ 
                  px: 3, 
                  py: 1,
                  fontSize: '0.9rem',
                  boxShadow: '0 4px 14px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(25, 118, 210, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Download Resume
              </Button>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<Email />}
                onClick={() => window.open('mailto:iyogesh.more1998@gmail.com', '_blank')}
                sx={{ 
                  px: 3, 
                  py: 1,
                  fontSize: '0.9rem',
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 14px rgba(25, 118, 210, 0.2)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Let's Talk
              </Button>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<EmojiEvents />}
                onClick={() => setCertificatesOpen(true)}
                sx={{ 
                  px: 3, 
                  py: 1,
                  fontSize: '0.9rem',
                  borderWidth: 2,
                  borderColor: 'secondary.main',
                  color: 'secondary.main',
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: 'secondary.main',
                    backgroundColor: 'rgba(220, 0, 78, 0.08)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 14px rgba(220, 0, 78, 0.2)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Certificates
              </Button>
            </Stack>


          </Box>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', mt: { xs: 4, md: 0 } }}>
            <Box sx={{ 
              position: 'relative',
              '@keyframes typing': {
                '0%': { width: '0%' },
                '50%': { width: '100%' },
                '100%': { width: '0%' },
              },
              '@keyframes fadeInOut': {
                '0%, 100%': { opacity: 0, transform: 'scale(0.8)' },
                '50%': { opacity: 1, transform: 'scale(1)' },
              },
            }}>
              {/* Typing Animation - Hidden on mobile for better performance */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: { xs: '10%', md: '-20%' },
                  transform: 'translateY(-50%)',
                  backgroundColor: darkMode ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.9)',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.primary.main}30`,
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: { xs: '10px', md: '12px' },
                  color: 'text.primary',
                  zIndex: 3,
                  animation: 'fadeInOut 4s ease-in-out infinite',
                  minWidth: { xs: '100px', md: '120px' },
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                <Box sx={{ 
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  borderRight: `2px solid ${theme.palette.primary.main}`,
                  animation: 'typing 3s steps(20) infinite',
                }}>
                  console.log("Hello!");
                </Box>
              </Box>

              {/* Main Photo */}
              <Avatar
                src="/yogesh.jpg"
                alt="Yogesh More"
                sx={{
                  width: { xs: 240, sm: 280, md: 380 },
                  height: { xs: 240, sm: 280, md: 380 },
                  border: `3px solid ${theme.palette.primary.main}`,
                  boxShadow: darkMode 
                    ? '0 20px 40px rgba(0,0,0,0.3)' 
                    : '0 20px 40px rgba(0,0,0,0.1)',
                  position: 'relative',
                  zIndex: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: darkMode 
                      ? '0 25px 50px rgba(0,0,0,0.4)' 
                      : '0 25px 50px rgba(0,0,0,0.15)',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>
      
      {/* Certificates Modal */}
      <CertificatesModal 
        open={certificatesOpen} 
        onClose={() => setCertificatesOpen(false)} 
      />
    </Box>
  );
};

export default Hero;